import { useEffect, useMemo, useState } from 'react';
import { TIMEZONE_OPTIONS } from './destination-options';
import styles from './export-composer.module.css';

// ─── R-55 / WU-3.6 — schedule editor ─────────────────────────────────────────
//
// The schedule lives on `ExportDestinationContent.schedule` and is synced
// into a `ScheduledExport` projection row on publish by
// `syncScheduledExportsToProduction`. Three editor concerns added in WU-3.6:
//
//   1. Cron client validation — server validates too, but surfacing the
//      error inline before publish saves a round-trip and a publish abort.
//   2. Timezone — IANA tz name persists on the schedule clause and
//      drives cron evaluation server-side.
//   3. Grouping mode — `per_doc | per_batch | rollup` flows through the
//      schema today; worker still treats every value as `per_doc` until
//      the Phase-10 follow-up ships per_batch + rollup execution paths.

type GroupingMode = 'per_doc' | 'per_batch' | 'rollup';

type Schedule = {
  enabled: boolean;
  cron: string;
  deliveryMethod?: string;
  deliveryConfig?: Record<string, unknown>;
  timezone?: string;
  groupingMode?: GroupingMode;
};

interface Props {
  schedule: Schedule | undefined;
  onChange: (schedule: Schedule) => void;
}

const DEFAULT_CRON = '0 17 * * *';

const PRESETS: { label: string; cron: string }[] = [
  { label: 'Every 15 min', cron: '*/15 * * * *' },
  { label: 'Hourly', cron: '0 * * * *' },
  { label: 'Daily at 5 PM', cron: '0 17 * * *' },
  { label: 'Weekly Mon 8 AM', cron: '0 8 * * 1' },
];


const GROUPING_OPTIONS: { value: GroupingMode; label: string; help: string }[] = [
  {
    value: 'per_doc',
    label: 'Per document',
    help: 'One file per document on each tick (current behaviour).',
  },
  {
    value: 'per_batch',
    label: 'Per batch',
    help: 'One aggregated file per batch (Phase-10 follow-up — falls back to per-doc today).',
  },
  {
    value: 'rollup',
    label: 'Rollup',
    help: 'Single aggregated file across all batches (Phase-10 follow-up — falls back to per-doc today).',
  },
];

function describeCron(cron: string): string {
  switch (cron) {
    case '*/15 * * * *':
      return 'Every 15 min';
    case '0 * * * *':
      return 'Hourly';
    case '0 17 * * *':
      return 'Daily at 5 PM';
    case '0 8 * * 1':
      return 'Weekly Mon 8 AM';
    default:
      return cron;
  }
}

/**
 * Lightweight cron validator. We don't ship `cron-parser` in the web bundle
 * (the API does the canonical validation server-side), so this is a syntax
 * pre-check that catches the common mistakes: wrong field count, illegal
 * characters, out-of-range numbers in obvious places. It's permissive on
 * purpose — the server is the authority and rejects bad expressions on
 * publish too.
 */
function validateCronClient(cron: string): string | null {
  const trimmed = cron.trim();
  if (!trimmed) return 'Cron expression is required';

  const fields = trimmed.split(/\s+/);
  if (fields.length !== 5) {
    return `Expected 5 fields (minute hour day-of-month month day-of-week), got ${fields.length}`;
  }

  // Each field: numbers, comma, dash, slash, asterisk, ?, and L/W chars
  // for day-of-week aliases. Reject anything else.
  const FIELD_RE = /^[*?/,\-0-9LW]+$/i;
  const NAMES = ['minute', 'hour', 'day-of-month', 'month', 'day-of-week'];
  for (let i = 0; i < 5; i++) {
    const f = fields[i]!;
    if (!FIELD_RE.test(f)) {
      return `Invalid characters in ${NAMES[i]} field: "${f}"`;
    }
  }

  return null;
}

export function SchedulePanel({ schedule, onChange }: Props) {
  // Debounced cron validation. The raw cron in state always reflects the
  // schedule clause, but we delay running validateCronClient until typing
  // settles for 200ms so the user doesn't see a flicker of "invalid" on
  // every keystroke.
  const [cronError, setCronError] = useState<string | null>(null);
  useEffect(() => {
    if (!schedule) {
      setCronError(null);
      return;
    }
    const handle = setTimeout(() => {
      setCronError(validateCronClient(schedule.cron));
    }, 200);
    return () => clearTimeout(handle);
  }, [schedule?.cron, schedule]);

  const description = useMemo(
    () => (schedule ? describeCron(schedule.cron) : ''),
    [schedule],
  );

  if (!schedule) {
    return (
      <div className={styles.schedulePanel}>
        <p className={styles.scheduleHint}>
          Documents are exported in real-time as they complete the pipeline. Add a
          schedule for batch exports.
        </p>
        <button
          type="button"
          className={styles.scheduleAddBtn}
          onClick={() =>
            onChange({
              enabled: false,
              cron: DEFAULT_CRON,
              timezone: 'UTC',
              groupingMode: 'per_doc',
            })
          }
        >
          Add Schedule
        </button>
      </div>
    );
  }

  const handleCronChange = (cron: string) => {
    onChange({ ...schedule, cron });
  };

  const handleEnabledChange = (enabled: boolean) => {
    onChange({ ...schedule, enabled });
  };

  const handleTimezoneChange = (timezone: string) => {
    onChange({ ...schedule, timezone });
  };

  const handleGroupingChange = (groupingMode: GroupingMode) => {
    onChange({ ...schedule, groupingMode });
  };

  const effectiveTimezone = schedule.timezone ?? 'UTC';
  const effectiveGrouping = schedule.groupingMode ?? 'per_doc';

  return (
    <div className={styles.schedulePanel}>
      <label className={styles.scheduleToggle}>
        <input
          type="checkbox"
          checked={schedule.enabled}
          onChange={(e) => handleEnabledChange(e.target.checked)}
        />
        <span>Enable scheduled export</span>
      </label>

      <div className={styles.scheduleField}>
        <label className={styles.scheduleLabel} htmlFor="schedule-cron">Cron expression</label>
        <input
          id="schedule-cron"
          type="text"
          className={styles.scheduleCronInput}
          value={schedule.cron}
          onChange={(e) => handleCronChange(e.target.value)}
          placeholder="0 17 * * *"
          spellCheck={false}
          aria-invalid={cronError ? true : undefined}
          aria-describedby={cronError ? 'schedule-cron-error' : undefined}
        />
        <span className={styles.scheduleCronDescription}>{description}</span>
      </div>
      {cronError && (
        <p
          id="schedule-cron-error"
          role="alert"
          data-testid="schedule-cron-error"
          className={styles.scheduleCronError}
        >
          {cronError}
        </p>
      )}

      <div className={styles.scheduleField}>
        <label className={styles.scheduleLabel} htmlFor="schedule-timezone">Timezone</label>
        <select
          id="schedule-timezone"
          className={styles.scheduleTimezoneSelect}
          value={effectiveTimezone}
          onChange={(e) => handleTimezoneChange(e.target.value)}
        >
          {TIMEZONE_OPTIONS.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
        <span className={styles.scheduleCronDescription}>
          Cron is evaluated in this zone server-side.
        </span>
      </div>

      <div className={styles.scheduleField}>
        <label className={styles.scheduleLabel} htmlFor="schedule-grouping">Grouping</label>
        <select
          id="schedule-grouping"
          className={styles.scheduleTimezoneSelect}
          value={effectiveGrouping}
          onChange={(e) => handleGroupingChange(e.target.value as GroupingMode)}
        >
          {GROUPING_OPTIONS.map((g) => (
            <option key={g.value} value={g.value}>
              {g.label}
            </option>
          ))}
        </select>
        <span className={styles.scheduleCronDescription}>
          {GROUPING_OPTIONS.find((g) => g.value === effectiveGrouping)?.help}
        </span>
      </div>

      <div className={styles.scheduleCronHelp} data-testid="schedule-cron-help">
        <p className={styles.scheduleCronHelpIntro}>
          Cron expression — minute hour day-of-month month day-of-week.
        </p>
        <ul className={styles.scheduleCronHelpExamples}>
          <li>
            <code>0 * * * *</code> hourly
          </li>
          <li>
            <code>0 0 * * *</code> daily at midnight
          </li>
          <li>
            <code>0 9 * * 1-5</code> weekdays at 9 AM
          </li>
          <li>
            <code>*/15 * * * *</code> every 15 min
          </li>
        </ul>
        <a
          className={styles.scheduleCronHelpLink}
          href={`https://crontab.guru/#${encodeURIComponent(schedule.cron)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Validate on crontab.guru →
        </a>
      </div>

      <div className={styles.schedulePresets}>
        {PRESETS.map((preset) => (
          <button
            key={preset.cron}
            type="button"
            className={styles.schedulePresetBtn}
            onClick={() => handleCronChange(preset.cron)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* WU-9.3 (R-55 secondary) — explain timing + grouping semantics.
          Cron now runs in `schedule.timezone` (default UTC); grouping is
          declarable but per-batch/rollup land in the Phase-10 worker
          follow-up referenced in apps/worker/src/jobs/scheduled-export.ts. */}
      <div className={styles.scheduleBehaviorNote} data-testid="schedule-behavior-note">
        <p className={styles.scheduleBehaviorNoteIntro}>
          <strong>How scheduled export behaves</strong>
        </p>
        <ul className={styles.scheduleBehaviorNoteList}>
          <li>
            <strong>Activation:</strong> schedules become active on <em>publish</em>,
            replacing any prior schedule on this destination.
          </li>
          <li>
            <strong>Timezone:</strong> cron runs in the selected zone (default UTC).
            Adjust the expression for that zone's local clock (e.g. <code>0 17 * * *</code>
            with <code>America/New_York</code> = 5 PM ET).
          </li>
          <li>
            <strong>Grouping:</strong> declare <code>per_doc</code> (one file per
            document, today's behaviour), <code>per_batch</code> (one file per batch),
            or <code>rollup</code> (single aggregated file). Per-batch and rollup are
            persisted today but executed as <code>per_doc</code> until the Phase-10
            follow-up.
          </li>
        </ul>
      </div>
    </div>
  );
}
